import type {
	FinancialPropertyListItem,
	ServicePeriodListItem,
} from "@/features/financialShared";
import {
	ServicePeriodStatus,
	toApiSortOrder,
} from "@/features/financialShared";
import type {
	CreateMonthlyServicePeriodBody,
	GetMonthlyServicePeriodsArgs,
	GetMonthlyStatementPropertiesArgs,
	MonthlyPeriodFileBody,
	MonthlyServicePeriodsResponse,
	MonthlyStatementPermissions,
	MonthlyStatementPropertiesResponse,
} from "./monthlyStatements.api.types";
import { mapMonthlyStatusFromApi } from "./monthlyStatements.api.types";

function asRecord(value: unknown): Record<string, unknown> | null {
	if (!value || typeof value !== "object" || Array.isArray(value)) return null;
	return value as Record<string, unknown>;
}

function asString(value: unknown, fallback = ""): string {
	return typeof value === "string" ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : fallback;
}

function asBoolean(value: unknown, fallback = false): boolean {
	if (typeof value === "boolean") return value;
	return fallback;
}

function unwrapData(raw: unknown): Record<string, unknown> {
	const root = asRecord(raw) ?? {};
	return asRecord(root.data) ?? root;
}

function formatShortDate(value: string): string {
	const trimmed = value.trim();
	if (!trimmed) return "";
	const date = new Date(trimmed);
	if (Number.isNaN(date.getTime())) return trimmed;
	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

function parseNamedPerson(raw: unknown): string {
	if (typeof raw === "string") return raw.trim();
	const row = asRecord(raw);
	if (!row) return "";
	return asString(row.name).trim();
}

function parseLatestPeriodLabel(raw: unknown): string {
	if (!raw) return "—";
	if (typeof raw === "string") return raw.trim() || "—";
	const row = asRecord(raw);
	if (!row) return "—";
	return asString(row.label).trim() || "—";
}

function parsePropertyItem(raw: unknown): FinancialPropertyListItem | null {
	const row = asRecord(raw);
	if (!row) return null;
	const id = asString(row.propertyId ?? row.id).trim();
	if (!id) return null;
	const name = asString(row.propertyName ?? row.name).trim();
	return {
		id,
		name: name || "Untitled property",
		totalUnits: asNumber(row.totalUnits),
		latestServicePeriodLabel: parseLatestPeriodLabel(row.latestServicePeriod),
		status: mapMonthlyStatusFromApi(row.status),
		lastUpdatedDate:
			asString(row.lastUpdatedDisplay).trim() ||
			formatShortDate(asString(row.lastUpdatedDate)) ||
			"—",
		approver: parseNamedPerson(row.operatedBy) || "—",
	};
}

export function parseMonthlyStatementPropertiesResponse(
	raw: unknown,
): MonthlyStatementPropertiesResponse {
	const data = unwrapData(raw);
	const items = Array.isArray(data.items)
		? data.items
				.map(parsePropertyItem)
				.filter((item): item is FinancialPropertyListItem => Boolean(item))
		: [];
	const page = Math.max(1, asNumber(data.page, 1));
	const pageSize = Math.max(1, asNumber(data.pageSize, 10));
	const total = asNumber(data.total, items.length);
	const totalPages = Math.max(1, asNumber(data.totalPages, 1));
	const lastSyncDisplay = asString(data.lastSyncDisplay).trim();
	const permissions = asRecord(data.permissions) ?? {};
	return {
		pageTitle: asString(data.pageTitle, "Monthly Statement"),
		lastSyncLabel: lastSyncDisplay ? `Last Sync: ${lastSyncDisplay}` : "",
		items,
		page,
		pageSize,
		total,
		totalPages,
		hasPrevious: asBoolean(data.hasPrevious, page > 1),
		hasNext: asBoolean(data.hasNext, page < totalPages),
		permissions: {
			canEdit: asBoolean(permissions.canEdit, true),
			canPublish: asBoolean(permissions.canPublish, false),
		} satisfies MonthlyStatementPermissions,
	};
}

function parsePeriodItem(
	raw: unknown,
	propertyId: string,
): ServicePeriodListItem | null {
	const row = asRecord(raw);
	if (!row) return null;
	const id = asString(row.id).trim();
	if (!id) return null;
	const hasUploadedData = asBoolean(row.hasUploadedData, false);
	const uploadedByName = parseNamedPerson(row.uploadedBy);
	const lastUpdatedByName = parseNamedPerson(row.lastUpdatedBy);
	const unitsCount = asNumber(row.unitsCount);
	const label =
		asString(row.label).trim() ||
		[asString(row.periodStart), asString(row.periodEnd)]
			.filter(Boolean)
			.join(" – ") ||
		"Untitled period";
	return {
		id,
		propertyId: asString(row.propertyId, propertyId),
		periodLabel: label,
		paidUnitsLabel: String(unitsCount),
		status: hasUploadedData
			? ServicePeriodStatus.PUBLISHED
			: ServicePeriodStatus.DRAFT,
		createdDate: formatShortDate(asString(row.createdAt)) || "—",
		lastUpdatedBy: lastUpdatedByName || "—",
		lastUpdatedDate: formatShortDate(asString(row.updatedAt)) || "—",
		approver: lastUpdatedByName || "—",
		startDate: formatShortDate(asString(row.periodStart)) || "—",
		endDate: formatShortDate(asString(row.periodEnd)) || "—",
		unitsLabel: String(unitsCount),
		uploadedDate: hasUploadedData
			? formatShortDate(asString(row.uploadedDate)) || "—"
			: "Not uploaded",
		uploadedBy: uploadedByName || "—",
		isUploaded: hasUploadedData,
		periodStartIso: asString(row.periodStart).trim() || undefined,
		periodEndIso: asString(row.periodEnd).trim() || undefined,
		balanceAsOfDateIso: asString(row.balanceAsOfDate).trim() || undefined,
		uploadedAtLabel: uploadedByName
			? `Uploaded by ${uploadedByName}`
			: hasUploadedData
				? "Data uploaded"
				: "No data uploaded",
	};
}

export function parseMonthlyServicePeriodsResponse(
	raw: unknown,
	propertyId: string,
): MonthlyServicePeriodsResponse {
	const data = unwrapData(raw);
	const resolvedPropertyId = asString(data.propertyId, propertyId);
	const items = Array.isArray(data.items)
		? data.items
				.map((item) => parsePeriodItem(item, resolvedPropertyId))
				.filter((item): item is ServicePeriodListItem => Boolean(item))
		: [];
	const permissions = asRecord(data.permissions) ?? {};
	const lastSyncDisplay = asString(data.lastSyncDisplay).trim();
	return {
		propertyId: resolvedPropertyId,
		propertyName: asString(data.propertyName).trim(),
		location: asString(data.location).trim(),
		uploadedPeriodsCount: asNumber(data.uploadedPeriodsCount),
		lastSyncLabel: lastSyncDisplay ? `Last Sync: ${lastSyncDisplay}` : "",
		items,
		page: Math.max(1, asNumber(data.page, 1)),
		pageSize: Math.max(1, asNumber(data.pageSize, 10)),
		total: asNumber(data.total, items.length),
		totalPages: Math.max(1, asNumber(data.totalPages, 1)),
		canCreate: asBoolean(permissions.canCreate, true),
		emptyMessage: asString(data.emptyMessage).trim() || "No periods found.",
	};
}

export function buildMonthlyStatementListParams(
	args: GetMonthlyStatementPropertiesArgs,
): Record<string, string | number> {
	const sortBy =
		args.sortBy === "TOTAL_UNITS" || args.sortBy === "LAST_UPDATED"
			? args.sortBy
			: "NAME";
	const params: Record<string, string | number> = {
		sortBy,
		sortOrder: toApiSortOrder(args.sortOrder),
		page: args.page ?? 1,
		pageSize: args.pageSize ?? 10,
	};
	if (args.search?.trim()) params.search = args.search.trim();
	return params;
}

export function buildMonthlyServicePeriodListParams(
	args: GetMonthlyServicePeriodsArgs,
): Record<string, string | number> {
	const params: Record<string, string | number> = {
		page: args.page ?? 1,
		pageSize: args.pageSize ?? 10,
	};
	if (args.search?.trim()) params.search = args.search.trim();
	return params;
}

export {
	parseDownloadedFilename,
	triggerBrowserDownload,
} from "@/features/financialShared";

export function spreadsheetMimeType(file: File): string {
	if (file.type) return file.type;
	if (file.name.toLowerCase().endsWith(".xls")) {
		return "application/vnd.ms-excel";
	}
	return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
}

export function toMonthlyPeriodFileBody(
	file: File,
	filePath: string,
): MonthlyPeriodFileBody {
	return {
		filePath,
		fileName: file.name,
		mimeType: spreadsheetMimeType(file),
		fileSize: file.size,
	};
}

export function buildCreateMonthlyServicePeriodBody(payload: {
	startDate: string;
	endDate: string;
	balanceAsOfDate: string;
	file?: MonthlyPeriodFileBody;
}): CreateMonthlyServicePeriodBody {
	return {
		periodStart: payload.startDate,
		periodEnd: payload.endDate,
		balanceAsOfDate: payload.balanceAsOfDate,
		...(payload.file ? { file: payload.file } : {}),
	};
}
